import { ChangeDetectionStrategy, Component, inject, Input, OnChanges, OnInit, signal, SimpleChanges } from '@angular/core';
import { RequestAttachment } from '@api/models/request.model';
import { RequestsService } from '@api/services/requests.service';
import { Button, ButtonComponent } from '@ui/button/button.component';
import { CardComponent } from '@ui/card/card.component';
import { IconComponent } from '@ui/icon/icon.component';
import { UploadDocumentsModalComponent } from '../upload-documents-modal/upload-documents-modal.component';
import { ModalService } from '@ui/modal/modal.service';
import { LoadingService } from '@ui/app-layout/loading-service';
import { CommonModule, DatePipe } from '@angular/common';

@Component({
  selector: 'app-requests-documents',
  standalone: true,
  imports: [CardComponent, IconComponent, CommonModule],
  template: `<ui-card
    [title]="'Documenten'"
    [buttons]="buttons()"
    [loading]="loadingAttachments()"
  >
    <div *ngFor="let attachment of attachments()">
      <div
        class="flex items-center justify-between p-3 hover:bg-gray-50 rounded transition-colors group cursor-pointer"
      >
        <div
          class="flex flex-row items-center gap-3 justify-between w-full"
          (click)="downloadAttachment(attachment.id, attachment.filename)"
        >
          <div class="flex items-center gap-3">
            <ui-icon [name]="'document-text'" [color]="'secondary'"></ui-icon>
            <div>
              <div class="text-sm font-medium">
                {{ attachment.filename }}
              </div>
              <div class="text-xs text-gray-500">
                {{ attachment.created_at | date : 'dd-MM-yyyy' }}
              </div>
            </div>
          </div>
          <ui-icon [name]="'download'" [color]="'secondary'"></ui-icon>
        </div>
      </div>
    </div>
  </ui-card>`,
  styles: `
    :host {
      display: block;
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RequestsDocumentsComponent implements OnInit, OnChanges {
  @Input() requestId: string | null = null;
  @Input() canUpload: boolean = false;
  attachments = signal<RequestAttachment[]>([]);
  loadingAttachments = signal(true);
  requestsService = inject(RequestsService);
  modalService = inject(ModalService);
  loadingService = inject(LoadingService);

  buttons = signal<Button[]>([]);

  constructor() {}

  ngOnInit() {
    this.getRequestAttachments();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['canUpload']) {
      this.canUpload = changes['canUpload'].currentValue;
      if (this.canUpload) {
        this.buttons.set([{
          label: 'Documenten uploaden',
          icon: 'upload',
          variant: 'primary',
          iconOnly: true,
          hideLabel: true,
          onClick: () => this.uploadDocuments(),
        }]);
      }
    }
  }

  getRequestAttachments() {
    let requestId = this.requestId;
    if (!requestId) {
      throw new Error('Request ID is required');
    }
    this.loadingAttachments.set(true);
    this.requestsService
      .getRequestAttachments(requestId)
      .subscribe((attachments) => {
        this.attachments.set(attachments);
        this.loadingAttachments.set(false);
      });
  }

  downloadAttachment(attachmentId: string, filename: string) {
    let requestId = this.requestId;
    if (!requestId) {
      throw new Error('Request ID is required');
    }

    this.loadingService.show();
    this.requestsService
      .downloadRequestAttachment(requestId, attachmentId)
      .subscribe((blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename; // The filename will come from the Content-Disposition header
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        this.loadingService.hide();
      });
  }

  uploadDocuments() {
    const modalRef = this.modalService.open(UploadDocumentsModalComponent, {
      data: {
        requestId: this.requestId || '',
      },
    });
    modalRef.onClose.subscribe(() => {
      this.getRequestAttachments();
    });
  }
}
